import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, EventItem } from '@/types/api'

export interface EventQueryParams {
    unitId?: string
    keySearch?: string
    status?: number
    startDateFrom?: string
    startDateTo?: string
    createdAtFrom?: string
    createdAtTo?: string
    updatedAtFrom?: string
    updatedAtTo?: string
}

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(raw: SpringPage<T> | PaginatedResponse<T>): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

export async function fetchEvents(
    page: number = 0,
    size: number = 10,
    params: EventQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<EventItem>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<EventItem>>>('/v1/event-management/search', {
            params: { page, size, ...params },
            signal,
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách sự kiện.'
        throw new Error(message)
    }
}

export async function getEventById(id: string, signal?: AbortSignal): Promise<EventItem> {
    try {
        const response = await api.get<ApiEnvelope<EventItem>>(`/v1/event-management/detail/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy chi tiết sự kiện.'
        throw new Error(message)
    }
}

export async function createEvent(formData: FormData): Promise<ApiEnvelope<EventItem>> {
    try {
        const response = await api.post<ApiEnvelope<EventItem>>('/v1/event-management', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo sự kiện.'
        throw new Error(message)
    }
}

export async function updateEvent(id: string, formData: FormData): Promise<ApiEnvelope<EventItem>> {
    try {
        const response = await api.put<ApiEnvelope<EventItem>>(`/v1/event-management/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật sự kiện.'
        throw new Error(message)
    }
}

export async function deleteEvent(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.delete<ApiEnvelope<any>>(`/v1/event-management/${id}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa sự kiện.'
        throw new Error(message)
    }
}